// AdminOne API client — separate token, scope=adminone, TTL guard.
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const ADMIN_API = `${BACKEND_URL}/api/adminone`;
const TOKEN_LS = "carelogix_adminone_token";
const USER_LS = "carelogix_adminone_user";
const EXP_LS = "carelogix_adminone_exp";
const ORG_LS = "carelogix_adminone_org_id";

export function getAdminOrgId() {
  try { return localStorage.getItem(ORG_LS) || null; } catch { return null; }
}

export function setAdminOrgId(orgId) {
  try {
    if (orgId) localStorage.setItem(ORG_LS, orgId);
    else localStorage.removeItem(ORG_LS);
  } catch { /* ignore */ }
}

export function setAdminToken(token, user) {
  try {
    if (token) {
      localStorage.setItem(TOKEN_LS, token);
      if (user) localStorage.setItem(USER_LS, JSON.stringify(user));
      // best-effort exp
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload?.exp) localStorage.setItem(EXP_LS, String(payload.exp));
      } catch { /* ignore */ }
    } else {
      localStorage.removeItem(TOKEN_LS);
      localStorage.removeItem(USER_LS);
      localStorage.removeItem(EXP_LS);
    }
  } catch { /* ignore */ }
}

export function getAdminToken() {
  try {
    const tok = localStorage.getItem(TOKEN_LS);
    if (!tok) return null;
    const exp = parseInt(localStorage.getItem(EXP_LS) || "0", 10);
    if (exp && Date.now() / 1000 > exp) {
      setAdminToken(null);
      return null;
    }
    return tok;
  } catch {
    return null;
  }
}

export function getAdminUser() {
  try {
    const u = localStorage.getItem(USER_LS);
    return u ? JSON.parse(u) : null;
  } catch {
    return null;
  }
}

const http = axios.create({ baseURL: ADMIN_API, timeout: 60000 });
http.interceptors.request.use((cfg) => {
  const t = getAdminToken();
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  const org = getAdminOrgId();
  if (org) {
    cfg.headers = cfg.headers || {};
    cfg.headers["X-Org-Id"] = org;
  }
  return cfg;
});
http.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      setAdminToken(null);
    }
    return Promise.reject(err);
  }
);

export const adminApi = {
  login: (email, password, totp_code) => http.post("/auth/login", { email, password, totp_code }),
  logout: () => http.post("/auth/logout"),
  me: () => http.get("/auth/me"),
  refresh: () => http.post("/auth/refresh"),
  dashboard: () => http.get("/dashboard"),
  oracleFeed: (params) => http.get("/oracle-feed", { params }),
  oandaStatus: () => http.get("/oanda/status"),
  oandaPrices: () => http.get("/oanda/prices"),
  oandaTest: () => http.post("/oanda/test"),
  reports: () => http.get("/reports"),
  complianceFlags: (status) => http.get("/compliance/flags", { params: { status } }),
  patchComplianceFlag: (id, body) => http.patch(`/compliance/flags/${id}`, body),
  audit: (params) => http.get("/audit", { params }),
  users: () => http.get("/users"),
  sessions: (limit = 100) => http.get("/sessions", { params: { limit } }),
  sessionGet: (id) => http.get(`/sessions/${id}`),
  sessionPatch: (id, body) => http.patch(`/sessions/${id}`, body),
  twofaStatus: () => http.get("/auth/2fa/status"),
  twofaSetup: () => http.post("/auth/2fa/setup"),
  twofaVerify: (code) => http.post("/auth/2fa/verify", { code }),
  twofaDisable: (code) => http.post("/auth/2fa/disable", { code }),
  auditCsvUrl: () => `${ADMIN_API}/audit/export.csv`,
  settings: () => http.get("/settings"),
  patchSettings: (body) => http.patch("/settings", body),
  // ---- v2.0 Signal Fabric / Watchtowers / Scores / Notification ----
  signals: (params) => http.get("/signals", { params }),
  createSignal: (body) => http.post("/signals", body),
  patchSignal: (id, body) => http.patch(`/signals/${id}`, body),
  escalateSignal: (id, body) => http.post(`/signals/${id}/escalate`, body || {}),
  signalHeatmap: () => http.get("/signals/heatmap"),
  signalTimeline: (params) => http.get("/signals/timeline", { params }),
  signalLifecycle: () => http.get("/signals/lifecycle"),
  signalEscalations: (limit = 50) => http.get("/signals/escalations", { params: { limit } }),
  signalCorrelations: (limit = 50) => http.get("/signals/correlations", { params: { limit } }),
  signalCorrelationsFor: (id) => http.get(`/signals/${id}/correlations`),
  scoresTop: (limit = 20) => http.get("/scores/top", { params: { limit } }),
  scoreByAsset: (asset) => http.get(`/scores/${encodeURIComponent(asset)}`),
  watchtowersSummary: () => http.get("/watchtowers/summary"),
  watchtowerEvents: (wt, limit = 25) => http.get(`/watchtowers/${wt}/events`, { params: { limit } }),
  chyronLive: (limit = 14) => http.get("/chyron/live", { params: { limit } }),
  channelsStatus: () => http.get("/channels/status"),
  channelsTest: (body) => http.post("/channels/test", body || {}),
  membershipTiers: () => http.get("/memberships/tiers"),
  setUserTier: (uid, tier) => http.patch(`/users/${uid}/tier`, { tier }),
  getUserTier: (uid) => http.get(`/users/${uid}/tier`),
  seedDemo: () => http.post("/seed/demo"),
  // ---- v2.1 Service Intelligence ----
  services: (params) => http.get("/services", { params }),
  servicesSummary: () => http.get("/services/summary"),
  service: (key) => http.get(`/services/${key}`),
  patchService: (key, body) => http.patch(`/services/${key}`, body),
  // ---- v2.1 Healthcare Map ----
  healthcareMap: (params) => http.get("/healthcare-map", { params }),
  healthcareMapSummary: () => http.get("/healthcare-map/summary"),
  healthcareMapForService: (key, limit = 25) => http.get(`/healthcare-map/services/${key}`, { params: { limit } }),
  // ---- v2.1 Operations Center ----
  opsSearch: (q, limit = 8) => http.get("/ops/search", { params: { q, limit } }),
  opsAlerts: (limit = 12) => http.get("/ops/alerts", { params: { limit } }),
  opsExecutive: () => http.get("/ops/executive"),
  opsQuickAction: (action) => http.post("/ops/quick-action", { action }),
  // ---- v3 Membership Constitution ----
  membershipMe: () => http.get("/memberships/me"),
  membershipFeatureMatrix: () => http.get("/memberships/feature-matrix"),
  membershipCheck: (body) => http.post("/memberships/check", body),
  membershipAnalytics: () => http.get("/memberships/analytics"),
  membershipDistribution: () => http.get("/memberships/analytics/distribution"),
  membershipRevenue: () => http.get("/memberships/analytics/revenue"),
  membershipFunnel: () => http.get("/memberships/analytics/funnel"),
  membershipAudit: (limit = 50) => http.get("/memberships/audit", { params: { limit } }),
  setUserTierEx: (uid, body) => http.patch(`/users/${uid}/tier`, body),
  // ---- v2.1 Notification Fabric catalog ----
  notificationsEvents: () => http.get("/notifications/events"),
  // ---- Phase 37 — Grants/Funding Inbox (CEO-only) ----
  inboxList: (params) => http.get("/inbox", { params }),
  inboxSummary: () => http.get("/inbox/summary"),
  inboxRefresh: () => http.post("/inbox/refresh"),
  inboxMarkRead: (notifId) => http.post(`/inbox/${encodeURIComponent(notifId)}/mark-read`),
  inboxDismiss: (notifId) => http.post(`/inbox/${encodeURIComponent(notifId)}/dismiss`),
  // ---- Phase 38 — Core-Satellite Intelligence Dashboard (CEO-only) ----
  coreSatelliteWidgets: () => http.get("/core-satellite/widgets"),
  // ---- v13 Telemetry ----
  telemetryRecent: (limit = 200) => http.get("/telemetry/recent", { params: { limit } }),
  telemetryByFeature: (hours = 24) => http.get("/telemetry/by-feature", { params: { hours } }),
  telemetryByTier: (hours = 24) => http.get("/telemetry/by-tier", { params: { hours } }),
  telemetryByEvent: (hours = 24) => http.get("/telemetry/by-event", { params: { hours } }),
  telemetryTimeseries: (hours = 24, bucket_minutes = 60) => http.get("/telemetry/timeseries", { params: { hours, bucket_minutes } }),
  telemetryRecord: (body) => http.post("/telemetry/record", body),
  // ---- v13 Rate Limits ----
  rateLimitTiers: () => http.get("/rate-limits/tiers"),
  rateLimitMe: () => http.get("/rate-limits/me"),
  rateLimitForUser: (uid) => http.get(`/rate-limits/user/${uid}`),
  rateLimitPreview: (tier, used = 0) => http.get("/rate-limits/preview", { params: { tier, used } }),
  // ---- v13 Organizations ----
  orgs: (limit = 100) => http.get("/orgs", { params: { limit } }),
  orgsSummary: () => http.get("/orgs/summary"),
  createOrg: (body) => http.post("/orgs", body),
  getOrg: (id) => http.get(`/orgs/${id}`),
  patchOrg: (id, body) => http.patch(`/orgs/${id}`, body),
  deleteOrg: (id) => http.delete(`/orgs/${id}`),
  orgMembers: (id) => http.get(`/orgs/${id}/members`),
  addOrgMember: (id, body) => http.post(`/orgs/${id}/members`, body),
  removeOrgMember: (id, uid) => http.delete(`/orgs/${id}/members/${uid}`),
  orgTeams: (id) => http.get(`/orgs/${id}/teams`),
  createOrgTeam: (id, body) => http.post(`/orgs/${id}/teams`, body),
  deleteOrgTeam: (teamId) => http.delete(`/orgs/teams/${teamId}`),
  orgInvites: (id) => http.get(`/orgs/${id}/invites`),
  createOrgInvite: (id, body) => http.post(`/orgs/${id}/invites`, body),
  revokeOrgInvite: (inviteId) => http.post(`/orgs/invites/${inviteId}/revoke`),
  // ---- v14 Ingestion ----
  ingestionFeeds: () => http.get("/ingestion/feeds"),
  ingestionRuns: (limit = 25) => http.get("/ingestion/runs", { params: { limit } }),
  ingestionItems: (limit = 50, feed = null) => http.get("/ingestion/items", { params: feed ? { limit, feed } : { limit } }),
  ingestionRun: () => http.post("/ingestion/run"),
  // ---- Phase 18 · Global Intelligence Chyron™ management ----
  chyronFeed: (visibility = "adminone", limit = 50) => http.get("/chyron", { params: { visibility, limit } }),
  chyronItems: (status = null, limit = 100) => http.get("/chyron/items", { params: status ? { status, limit } : { limit } }),
  chyronItemCreate: (body) => http.post("/chyron/items", body),
  chyronItemArchive: (id) => http.delete(`/chyron/items/${id}`),
  chyronSettingsGet: () => http.get("/chyron/settings"),
  chyronSettingsPatch: (body) => http.patch("/chyron/settings", body),
  // ─── Phase 24 — Uganda 🇺🇬 Phase 2 Real Infrastructure Activation ───
  ugandaPhase2Status:      () => http.get("/uganda-phase2/status"),
  ugandaPhase2Activations: () => http.get("/uganda-phase2/activations"),
  ugandaPhase2LiveMap:     () => http.get("/uganda-phase2/live-map"),
  ugandaPhase2Seed:        () => http.post("/uganda-phase2/seed"),
  ugandaPhase2GetById:     (id) => http.get(`/uganda-phase2/activation/${id}`),
  // ─── Phase 25 — ORACLE Intelligence Layer™ + NiSi Predictive Engine ───
  intelligenceStatus:       () => http.get("/intelligence/status"),
  intelligenceDashboard:    () => http.get("/intelligence/dashboard"),
  intelligenceTopOpps:      (limit = 12) => http.get("/intelligence/top-opportunities", { params: { limit } }),
  intelligenceHighestConf:  (limit = 8) => http.get("/intelligence/highest-confidence", { params: { limit } }),
  intelligenceHeatmap:      () => http.get("/intelligence/heatmap"),
  intelligenceFxStrength:   () => http.get("/intelligence/forex-strength"),
  intelligenceForex:        () => http.get("/intelligence/forex"),
  intelligenceCrypto:       (limit = 8) => http.get("/intelligence/crypto", { params: { limit } }),
  intelligenceSports:       () => http.get("/intelligence/sports"),
  intelligenceHealthcare:   () => http.get("/intelligence/healthcare"),
  intelligenceAfrica:       () => http.get("/intelligence/africa"),
  intelligenceGlobalAI:     () => http.get("/intelligence/global-ai"),
  intelligenceBiotech:      () => http.get("/intelligence/biotech"),
  intelligenceDailyBrief:   () => http.get("/intelligence/daily-brief"),
  intelligenceChyron:       (limit = 14) => http.get("/intelligence/chyron", { params: { limit } }),
  // ─── Phase 26 — Startups + Grants + Briefing Center ───
  startupsPublic:           () => axios.get(`${BACKEND_URL}/api/oracle/startups`),
  grantsPublic:             () => axios.get(`${BACKEND_URL}/api/oracle/grants`),
  startupsGrantsSummary:    () => axios.get(`${BACKEND_URL}/api/oracle/startup-grants/summary`),
  briefingCenterStatus:     () => axios.get(`${BACKEND_URL}/api/adminone/briefing-center/status`),
  briefingCenterFull:       () => http.get("/briefing-center/full"),
  briefingCenterChannels:   () => http.get("/briefing-center/channels"),
};

// ─── Phase 16 — LOGIX-2™ client (mounted at /api/logix2, no admin token required) ───
const logix2Http = axios.create({ baseURL: `${BACKEND_URL}/api/logix2`, timeout: 15000 });
export const logix2Api = {
  status: () => logix2Http.get("/status"),
  capabilities: () => logix2Http.get("/capabilities"),
  grades: () => logix2Http.get("/score/grades"),
  signalTaxonomy: () => logix2Http.get("/signals/taxonomy"),
  watchtowersV2: () => logix2Http.get("/watchtowers/v2"),
  routing: () => logix2Http.get("/routing/manifest"),
  horizons: () => logix2Http.get("/opportunities/horizons"),
  opportunityRadar: ({ horizon = "today", top_n = 10, domain = null } = {}) =>
    logix2Http.get("/opportunities/radar", { params: domain ? { horizon, top_n, domain } : { horizon, top_n } }),
  scoreCompute: (components) => logix2Http.post("/score/compute", { components }),
  // Phase 17 ops
  adaptersStatus: () => logix2Http.get("/adapters/status"),
  adaptersProbe: () => logix2Http.get("/adapters/probe"),
  schedulerStatus: () => logix2Http.get("/scheduler/status"),
  schedulerTrigger: () => logix2Http.post("/scheduler/trigger"),
  opportunityRadarLive: ({ horizon = "today", top_n = 10 } = {}) =>
    logix2Http.get("/opportunities/radar-live", { params: { horizon, top_n } }),
};

// ─── Phase 16.6 — LOGIX-2 Bottom Intelligence Dock client ───
const logix2DockHttp = axios.create({ baseURL: `${BACKEND_URL}/api/logix2`, timeout: 60000 });
// Attach X-User-Id for ORACLE public users when present (browser-side localStorage)
logix2DockHttp.interceptors.request.use((c) => {
  try {
    const uid = localStorage.getItem("carelogix_oracle_user_id");
    if (uid) c.headers = { ...(c.headers || {}), "X-User-Id": uid };
    const tk = localStorage.getItem("carelogix_oracle_token");
    if (tk) c.headers = { ...(c.headers || {}), Authorization: `Bearer ${tk}` };
  } catch { /* ignore */ }
  return c;
});
export const logix2Dock = {
  memberAccess: () => logix2DockHttp.get("/member-access"),
  dailyBrief: (domain = "all") => logix2DockHttp.get("/daily-brief", { params: { domain } }),
  chat: ({ messages, session_id, domain, user_name }) => logix2DockHttp.post("/chat", { messages, session_id, domain, user_name }),
  watchlistGet: () => logix2DockHttp.get("/watchlist"),
  watchlistAdd: ({ symbol, note, domain }) => logix2DockHttp.post("/watchlist", { symbol, note, domain }),
  escalate: ({ symbol, summary, severity, domain }) =>
    logix2DockHttp.post("/escalate", { symbol, summary, severity, domain }),
  adminQueue: (limit = 50) => logix2DockHttp.get("/admin/queue", { params: { limit } }),

  // Phase 20 — Voice Intelligence Layer™
  voiceStatus: () => logix2DockHttp.get("/voice/status"),
  voiceGreeting: () => logix2DockHttp.get("/voice/greeting"),
  voiceSettings: () => logix2DockHttp.get("/voice/settings"),
  voiceSettingsPatch: (patch) => logix2DockHttp.patch("/voice/settings", patch),
  voiceSpeak: (body) => logix2DockHttp.post("/voice/speak", body),
  voiceTranscribe: (formData) => logix2DockHttp.post("/voice/transcribe", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  imageSearch: (formData) => logix2DockHttp.post("/image-search", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  voiceAccessSummary: () => logix2DockHttp.get("/voice/access-summary"),
};

// ─── Phase 16.5 — ORACLE diagnostics client (mounted at /api/oracle/diagnostics) ───
const oracleDiagHttp = axios.create({ baseURL: `${BACKEND_URL}/api/oracle`, timeout: 10000 });
export const oracleDiagApi = {
  diagnostics: () => oracleDiagHttp.get("/diagnostics"),
};
